import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import {
  Building2,
  CalendarDays,
  Check,
  ExternalLink,
  Pencil,
  Plus,
  Telescope,
  Trash2,
  X,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Select } from "@/components/ui/select"
import { ResearchPanel, type ResearchState } from "@/components/manufacturers/ResearchPanel"
import { ManufacturerSignalFormDialog } from "@/components/manufacturers/SignalFormDialog"
import { domainLabel, type Signal } from "@/lib/manufacturerSignals"

type Manufacturer = {
  id: number
  name: string
  canonical_name: string
  country: string | null
  notes: string | null
  is_own: boolean
  aliases: string[]
  researched: boolean
  last_researched_at: string | null
  created_at: string
}

type PageProps = {
  manufacturer: Manufacturer
  research: ResearchState
  signals: { approved: Signal[]; pending: Signal[] }
  signal_filters: { domain: string | null }
  signal_filter_options: { domains: string[] }
  taxonomy: { domains: string[] }
}

function SignalCard({
  signal,
  onEdit,
  onDelete,
  onApprove,
  onReject,
}: {
  signal: Signal
  onEdit: () => void
  onDelete: () => void
  onApprove?: () => void
  onReject?: () => void
}) {
  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        {signal.domain && <Badge tone="signal">{domainLabel(signal.domain)}</Badge>}
        <Badge tone={signal.origin === "manual" ? "accent" : "muted"}>
          {signal.origin === "manual" ? "Manual" : "Riset"}
        </Badge>
        {signal.observed_on && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
            <CalendarDays className="h-3 w-3" />
            {signal.observed_on}
          </span>
        )}
      </div>

      <div className="mt-2 font-medium text-ink-body">{signal.title}</div>
      {signal.body && <p className="mt-1 text-sm text-ink-body">{signal.body}</p>}

      {signal.source_url && (
        <a
          href={signal.source_url}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 break-all text-sm"
        >
          <ExternalLink className="h-3 w-3 shrink-0" />
          {signal.source_url}
        </a>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {onApprove && (
          <Button size="sm" variant="secondary" onClick={onApprove}>
            <Check className="mr-1 h-3.5 w-3.5" />
            Setujui
          </Button>
        )}
        {onReject && (
          <Button size="sm" variant="ghost" onClick={onReject}>
            <X className="mr-1 h-3.5 w-3.5" />
            Tolak
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onEdit}>
          <Pencil className="mr-1 h-3.5 w-3.5" />
          Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="mr-1 h-3.5 w-3.5" />
          Hapus
        </Button>
      </div>
    </div>
  )
}

export default function Show({
  manufacturer,
  research,
  signals,
  signal_filters,
  signal_filter_options,
  taxonomy,
}: PageProps) {
  const description = `Profil & intelijen manufaktur ${manufacturer.name}: riset mendalam, timeline sinyal kompetitif, dan entri manual.`

  const [formOpen, setFormOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Signal | null>(null)
  const [rejectTarget, setRejectTarget] = React.useState<Signal | null>(null)

  const openNew = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (signal: Signal) => {
    setEditing(signal)
    setFormOpen(true)
  }

  const signalPath = (signal: Signal) =>
    `/admin/manufacturers/${manufacturer.id}/signals/${signal.id}`

  const approve = (signal: Signal) => {
    router.patch(signalPath(signal), { signal: { status: "approved" } }, { preserveScroll: true })
  }
  const confirmReject = () => {
    if (!rejectTarget) return
    router.patch(
      signalPath(rejectTarget),
      { signal: { status: "rejected" } },
      { preserveScroll: true, onFinish: () => setRejectTarget(null) },
    )
  }
  const destroy = (signal: Signal) => {
    if (!confirm(`Hapus sinyal "${signal.title}"?`)) return
    router.delete(signalPath(signal), { preserveScroll: true })
  }

  const onDeleteManufacturer = () => {
    if (!confirm(`Hapus manufaktur "${manufacturer.name}"? Tindakan ini tidak dapat dibatalkan.`))
      return
    router.delete(`/admin/manufacturers/${manufacturer.id}`)
  }

  const onFilterDomain = (domain: string) => {
    router.get(
      `/admin/manufacturers/${manufacturer.id}`,
      domain ? { domain } : {},
      { preserveScroll: true, preserveState: true, only: ["signals", "signal_filters"] },
    )
  }

  return (
    <>
      <Head title={manufacturer.name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={manufacturer.name} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Intelligence</span>
          <span>/</span>
          <Link href="/admin/manufacturers" className="hover:text-ink-body">
            Manufaktur
          </Link>
          <span>/</span>
          <span className="text-ink-body font-medium">{manufacturer.name}</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1>{manufacturer.name}</h1>
                {manufacturer.is_own && (
                  <Badge tone="accent">
                    <Building2 className="mr-1 h-3 w-3" />
                    Perusahaan sendiri
                  </Badge>
                )}
                {manufacturer.researched && (
                  <Badge tone="signal">
                    <Telescope className="mr-1 h-3 w-3" />
                    Sudah diriset
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-sm text-ink-muted">{description}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="secondary">
                <Link href={`/admin/manufacturers/${manufacturer.id}/edit`}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Link>
              </Button>
              <Button variant="danger" onClick={onDeleteManufacturer}>
                <Trash2 className="mr-2 h-4 w-4" />
                Hapus
              </Button>
            </div>
          </div>
        </div>

        {/* Profile */}
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="text-sm font-medium text-ink-muted">Identitas</div>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Nama kanonik</dt>
                <dd className="text-ink-body">{manufacturer.canonical_name}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Negara</dt>
                <dd className="text-ink-body">{manufacturer.country ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink-muted">Kepemilikan</dt>
                <dd className="text-ink-body">
                  {manufacturer.is_own ? "Perusahaan sendiri" : "Kompetitor"}
                </dd>
              </div>
            </dl>
          </section>

          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="text-sm font-medium text-ink-muted">Alias</div>
            {manufacturer.aliases.length === 0 ? (
              <p className="mt-3 text-sm text-ink-muted">Belum ada alias.</p>
            ) : (
              <div className="mt-3 flex flex-wrap gap-1">
                {manufacturer.aliases.map((a) => (
                  <Badge key={a} tone="muted">
                    {a}
                  </Badge>
                ))}
              </div>
            )}
          </section>

          {manufacturer.notes && (
            <section className="rounded-lg border border-hairline bg-surface p-5 sm:col-span-2">
              <div className="text-sm font-medium text-ink-muted">Catatan</div>
              <p className="mt-3 whitespace-pre-line text-sm text-ink-body">{manufacturer.notes}</p>
            </section>
          )}
        </div>

        {/* Deep-research trigger / progress */}
        <ResearchPanel
          manufacturerId={manufacturer.id}
          research={research}
          pendingResearchCount={signals.pending.length}
        />

        {/* Signals */}
        <section className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline pb-3">
            <div className="flex items-center gap-2 text-sm font-medium text-ink-body">
              <Telescope className="h-4 w-4 text-ink-muted" />
              Timeline Sinyal
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                aria-label="Filter domain"
                value={signal_filters.domain ?? ""}
                onChange={(e) => onFilterDomain(e.target.value)}
              >
                <option value="">Semua domain</option>
                {signal_filter_options.domains.map((d) => (
                  <option key={d} value={d}>
                    {domainLabel(d)}
                  </option>
                ))}
              </Select>
              <Button variant="secondary" onClick={openNew}>
                <Plus className="mr-2 h-4 w-4" />
                Tambah sinyal manual
              </Button>
            </div>
          </div>

          {/* Pending review */}
          {signals.pending.length > 0 && (
            <div className="mt-6 rounded-lg border border-signal bg-signal-faded p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-ink-body">Menunggu review</span>
                <Badge tone="muted">{signals.pending.length}</Badge>
              </div>
              <p className="mt-0.5 text-xs text-ink-muted">
                Sinyal hasil riset belum resmi hingga disetujui.
              </p>
              <div className="mt-4 flex flex-col gap-3">
                {signals.pending.map((s) => (
                  <SignalCard
                    key={s.id}
                    signal={s}
                    onEdit={() => openEdit(s)}
                    onDelete={() => destroy(s)}
                    onApprove={() => approve(s)}
                    onReject={() => setRejectTarget(s)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Approved timeline */}
          <div className="mt-6 flex flex-col gap-3">
            {signals.approved.length > 0 ? (
              signals.approved.map((s) => (
                <SignalCard
                  key={s.id}
                  signal={s}
                  onEdit={() => openEdit(s)}
                  onDelete={() => destroy(s)}
                />
              ))
            ) : (
              <div className="rounded-lg border border-dashed border-hairline bg-surface p-8 text-center">
                <p className="text-sm text-ink-muted">
                  Belum ada sinyal resmi.{" "}
                  {signal_filters.domain
                    ? "Coba ubah filter, atau tambahkan sinyal baru."
                    : "Jalankan riset mendalam atau tambahkan sinyal manual."}
                </p>
              </div>
            )}
          </div>
        </section>
      </AppShell>

      <ManufacturerSignalFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        manufacturerId={manufacturer.id}
        domains={taxonomy.domains}
        signal={editing}
      />

      <Dialog open={rejectTarget !== null} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Tolak sinyal?</DialogTitle>
            <DialogDescription>
              Sinyal “{rejectTarget?.title}” akan ditandai ditolak dan dikeluarkan dari timeline.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTarget(null)}>
              Batal
            </Button>
            <Button variant="danger" onClick={confirmReject}>
              Tolak
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
